import { UrlWithParsedQuery, parse } from 'url'
import _ from 'lodash'
import { AppSpecData, ViewerAppsUrls, AppUrlsArg } from '@wix/thunderbolt-symbols'
import {
	createDatabindingAppData,
	extractViewerScriptUrlOverrideMap,
	extractControllersUrlOverrideMap,
	extractOverridePlatformBaseUrls,
	extractWidgetsUrlOverrideMap,
} from './utils/csmManipulations'
import { resolveAppStudioViewerScriptUrls } from './utils/appSudioViewerScriptUrlResolves'

function urlJoin(...parts: Array<string>) {
	return parts.slice(1).reduce((result, part) => {
		if (result.endsWith('/') && part.startsWith('/')) {
			result += part.substr(1)
		} else if (!result.endsWith('/') && !part.startsWith('/')) {
			result += '/' + part
		} else {
			result += part
		}

		return result
	}, parts[0])
}

export function createViewerAppsUrlMap({
	rendererModel,
	serviceTopology,
	siteUrl,
	experiments,
}: AppUrlsArg): ViewerAppsUrls {
	const { clientSpecMap, dacVersionOverrides, metaSiteId } = rendererModel
	let requestUrl: UrlWithParsedQuery
	try {
		requestUrl = parse(decodeURIComponent(siteUrl), true)
	} catch (ex) {
		requestUrl = parse('', true)
	}
	const controllersOverridesMap = extractControllersUrlOverrideMap(requestUrl)
	const viewerScriptUrlOverridesMap = extractViewerScriptUrlOverrideMap(requestUrl)
	const additionalCsmValues: { [name: string]: any } = {}

	const wixCodeAppData = _.find(clientSpecMap, ['type', 'siteextension'])
	if (wixCodeAppData) {
		const dbsmViewerApp = serviceTopology.scriptsLocationMap['dbsm-viewer-app']
		const wixCodeViewerAppUrlBase = serviceTopology.scriptsLocationMap['wix-code-viewer-app']

		additionalCsmValues.fakeApplicationId = createDatabindingAppData(wixCodeAppData as any, dbsmViewerApp)
		const wixCodeAppDefinitionId = _.get(wixCodeAppData, 'appDefinitionId')
		additionalCsmValues[wixCodeAppDefinitionId] = {
			appDefinitionId: wixCodeAppDefinitionId,
			appFields: { platform: { viewerScriptUrl: `${wixCodeViewerAppUrlBase}/app.js` } },
		}
	}

	const allRelevantCsmApps = [clientSpecMap, additionalCsmValues].flatMap((csm) => {
		const apps = _.values(csm).filter((spec: AppSpecData) =>
			_.get(spec, ['appFields', 'platform', 'viewerScriptUrl'])
		)
		const [studioAppSpecs, platformApps] = _.partition(apps, (spec: AppSpecData) =>
			_.get(spec, ['appFields', 'platform', 'studio'])
		)

		const studioApps = resolveAppStudioViewerScriptUrls(studioAppSpecs, serviceTopology, experiments, metaSiteId)

		return [...platformApps, ...studioApps]
	})

	if (_.isEmpty(allRelevantCsmApps)) {
		return {}
	}

	// checks if the url contains Dac overrides as dacOverrides query param and adds them
	const updatedDacOverrides = {
		...dacVersionOverrides,
		...extractDacOverridesQueryParamFromUrl(requestUrl),
	}

	const baseUrlsOverrides = extractOverridePlatformBaseUrls(requestUrl)

	const appsUrlsArray = allRelevantCsmApps.map((specData: AppSpecData) => {
		const { appDefinitionId } = specData
		const {
			viewerScriptUrl,
			baseUrls,
			viewer,
			viewerScriptUrlTemplate,
			baseUrlsTemplate = {},
		} = specData.appFields.platform
		const serviceVersion = (artifactId: string) => {
			if (!updatedDacOverrides[artifactId]) {
				throw new MissingOverrideError(artifactId, 'serviceVersion')
			}
			return updatedDacOverrides[artifactId]
		}

		const serviceUrl = (artifactId: string, path: string) => {
			const override = updatedDacOverrides[artifactId]

			if (!override) {
				throw new MissingOverrideError(artifactId, 'serviceUrl')
			}

			if (override.startsWith('http://') || override.startsWith('https://')) {
				return urlJoin(override, path)
			} else {
				return urlJoin(serviceTopology.staticServerUrl, 'services', artifactId, override, path ?? '')
			}
		}

		const templateFunctions = { serviceUrl, serviceVersion }

		const getUrl = createUrlGetter(templateFunctions)

		const viewerScriptUrlOverride = viewerScriptUrlOverridesMap?.[appDefinitionId]
		const viewerWidgets = _.pickBy(specData.widgets, (widget) => widget.componentFields)
		const baseUrlsOverride = baseUrlsOverrides[appDefinitionId] || {}

		return {
			appDefId: specData.appDefinitionId,
			appDefName: specData.appDefinitionName,
			viewerScriptUrl: viewerScriptUrlOverride || getUrl(viewerScriptUrlTemplate, viewerScriptUrl),
			baseUrls: _.mapValues(baseUrls, (url, baseUrlName) => {
				return baseUrlsOverride[baseUrlName] || getUrl(baseUrlsTemplate[baseUrlName], url)
			}),
			errorReportingUrl: viewer?.errorReporting?.url,
			widgets: _.mapValues(viewerWidgets, (widget) => {
				const {
					componentUrl,
					componentUrlTemplate,
					controllerUrl,
					controllerUrlTemplate,
					noCssComponentUrl,
					noCssComponentUrlTemplate,
					isModuleFederated,
					cssPerBreakpoint,
				} = widget.componentFields

				const controllerUrlOverride = controllersOverridesMap?.[widget.widgetId]
				const widgetUrlOverride = extractWidgetsUrlOverrideMap(requestUrl)?.[widget.widgetId]

				return {
					controllerUrl: controllerUrlOverride || getUrl(controllerUrlTemplate, controllerUrl),
					componentUrl: widgetUrlOverride || getUrl(componentUrlTemplate, componentUrl),
					noCssComponentUrl: widgetUrlOverride || getUrl(noCssComponentUrlTemplate, noCssComponentUrl),
					errorReportingUrl:
						widget.componentFields.viewer?.errorReporting?.url || viewer?.errorReporting?.url,
					widgetId: widget.widgetId,
					isModuleFederated,
					cssPerBreakpoint,
				}
			}),
		}
	})

	return _.keyBy(appsUrlsArray, 'appDefId')
}

function createUrlGetter(templateFunctions: {
	serviceUrl: (artifactId: string, path: string) => string | undefined
	serviceVersion: (artifactId: string) => string
}) {
	return (urlTemplate: string = '', fallback: string = '') => {
		if (!urlTemplate || !isValidUrlTemplate(urlTemplate)) {
			// TODO: Report that the template is invalid (if there is one) ones we will have reporting available in this  context
			return fallback
		}

		const template = _.template(urlTemplate)
		try {
			const url = template(templateFunctions)

			if (!url) {
				// TODO: Report that the template returned a falsy value
				return fallback
			}

			return url
		} catch (err) {
			// TODO: Report that there was an error while parsing the url template
			return fallback
		}
	}
}

export const isValidUrlTemplate = (template: string): boolean => {
	// serviceUrls functions should be the only thing in the template and have 2 arguments passed to them always
	const isValidServiceUrlTemplateString = /^<%=\s*serviceUrl\(\s*(".+"|'.+')\s*,\s*(".+"|'.+')\s*\)\s*%>$/.test(
		template
	)

	// serviceVersions functions should be a part of a url i.e should be prefixed by some string and accept only one argument.
	const isValidServiceVersionTemplateString = /.+<%=\s*serviceVersion\(\s*(".+"|'.+')\s*\)\s*%>/.test(template)

	return isValidServiceUrlTemplateString || isValidServiceVersionTemplateString
}

function extractDacOverridesQueryParamFromUrl(url: UrlWithParsedQuery): { [name: string]: string } {
	const overrides = (Object.entries(url.query).filter(
		([key, value]) => key.endsWith('-override') && _.isString(value)
	) as Array<[string, string]>).map(([key, value]) => [key.substring(0, key.length - '-override'.length), value])
	return _.fromPairs(overrides)
}

class MissingOverrideError extends Error {
	constructor(artifactId: string, templateFunction: 'serviceUrl' | 'serviceVersion') {
		super(`Missing dac override for ${artifactId} in "${templateFunction}" template.`)
	}
}
